import React, { forwardRef, useCallback, useMemo } from "react"
import { useRemainWindowHeight } from "@/core/hooks"
import { ConfigProvider } from "antd"
import Icon from "../Icon"
import Empty, { renderEmpty } from "./Empty"
import { RowBuilder, CellBuilder } from "./Builder"
import { StyledTable } from "./material"
import { includes, isEmpty, isEqual, isPlainObject, map, omit, range, size } from "lodash"
import tw, { theme } from "twin.macro"
import { v4 as uuid } from "uuid"
import { clsx } from "@/utilities"
import { DEFAULT_EMPTY_TABLE_HEIGHT, getRowKey, OFFSET_BOTTOM } from "./helper"
import { COLUMN_SELECTION_WIDTH, PAGE_SIZE } from "@/constants/setting"

function BaseTable(
    {
        className,
        columns,
        dataSource,
        isLoading,
        rowKey,
        pagination,
        rowHoverable = true,
        rowHoverHighlight = rowHoverable,
        rowBackgroundStriped: striped = true,
        emptyDescription = "No data",
        emptyHeight,
        components,
        isEmptyFillScreen = true,
        sorter,
        onSort,
        ...props
    },
    fwRef
) {
    const [elTableRef, remainHeight] = useRemainWindowHeight({
        defaultHeight: DEFAULT_EMPTY_TABLE_HEIGHT,
        getElement: (el) => el?.nativeElement,
        offset: OFFSET_BOTTOM,
        enabled: isEmptyFillScreen,
        deps: [dataSource, isLoading]
    })

    const _dataSource = useMemo(() => {
        if (isLoading) {
            return range(0, size(dataSource) || PAGE_SIZE).map(() => ({ __uuid: uuid() }))
        }
        return map(dataSource, (record) => ({ ...record, __uuid: uuid() }))
    }, [dataSource, isLoading])

    const _columns = useMemo(() => {
        const cols = isLoading ? omit(columns, ["sorting"]) : columns

        return map(cols, ({ key, sorting, ...col }, index) => {
            const sortOrder = sorter[key]
            const iconSizes = [8, 4]
            const sortSequence = Object.keys(sorter).indexOf(key) + 1

            return {
                ...col,
                ...(isLoading && {
                    render: () => (
                        <div tw="w-full h-[18px] bg-gray200" css={[index === 1 && tw`h-[36px]`]} />
                    )
                }),
                key,
                sorter: sorting,
                shouldCellUpdate: (record, prevRecord) => !isEqual(record, prevRecord),
                sortIcon: () => {
                    return (
                        <div>
                            <div tw="ml-2 inline-block">
                                <Icon
                                    tw="transform rotate-180 mb-1"
                                    name="sort_down"
                                    sizes={iconSizes}
                                    {...(sortOrder === "asc" && {
                                        color: theme`colors.gray800`
                                    })}
                                />
                                <Icon
                                    name="sort_down"
                                    sizes={iconSizes}
                                    {...(sortOrder === "desc" && {
                                        color: theme`colors.gray800`
                                    })}
                                />
                            </div>
                            <div tw="text-[8px] inline-block ml-1">
                                {!!sortSequence && sortSequence}
                            </div>
                        </div>
                    )
                },
                onHeaderCell: () => ({
                    onClick: () => sorting && onSort(key)
                })
            }
        })
    }, [isLoading, columns, sorter, onSort])

    const propsEmpty = useMemo(
        () => ({
            description: emptyDescription,
            ...(remainHeight && { height: emptyHeight ? emptyHeight : remainHeight })
        }),
        [emptyDescription, remainHeight]
    )

    const setRef = (ref) => {
        elTableRef.current = ref
        if (isPlainObject(fwRef)) {
            fwRef.current = ref
        }
    }

    const buildRow = useCallback(
        (ownProps) => {
            return (
                <RowBuilder
                    {...ownProps}
                    builder={components?.body?.row}
                    rowSelection={props.rowSelection}
                />
            )
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [props.rowSelection?.selectedRowKeys]
    )

    const buildCell = useCallback(
        (ownProps) => {
            return (
                <CellBuilder
                    {...ownProps}
                    builder={components?.body?.cell}
                    rowSelection={props.rowSelection}
                />
            )
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [props.rowSelection?.selectedRowKeys]
    )

    return (
        <ConfigProvider renderEmpty={renderEmpty(propsEmpty)}>
            <StyledTable
                {...omit(props, isLoading ? ["rowSelection"] : [])}
                scroll={{
                    x:
                        isPlainObject(props.rowSelection) && props.scroll?.x && !isLoading
                            ? props.scroll?.x + COLUMN_SELECTION_WIDTH
                            : props.scroll?.x,
                    y: props.scroll?.y
                }}
                ref={setRef}
                className={clsx(
                    "atomic-table",
                    className,
                    isLoading && "atomic-loading-table",
                    isEmpty(dataSource) && "atomic-empty-table"
                )}
                css={[isEmptyFillScreen && remainHeight === null && tw`invisible`]}
                rowKey={getRowKey(rowKey, isLoading)}
                columns={_columns}
                dataSource={_dataSource}
                showSorterTooltip={false}
                rowHoverHighlight={rowHoverHighlight}
                rowHoverable={rowHoverable}
                striped={striped}
                locale={{
                    emptyText: <Empty {...propsEmpty} />
                }}
                components={{
                    ...components,
                    body: {
                        ...components?.body,
                        cell: buildCell,
                        row: buildRow
                    }
                }}
                pagination={pagination}
                {...(isPlainObject(pagination) && {
                    pagination: {
                        ...pagination,
                        ...(isEmpty(dataSource) && { total: 1 }),
                        showSizeChanger: false,
                        itemRender: (page, type, el) => {
                            if (includes(["next", "prev"], type)) {
                                return (
                                    <Icon
                                        name="arrow_left"
                                        color="currentColor"
                                        sizes={[7.5, 13.33]}
                                        tw="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                                        css={[type === "prev" && tw`transform rotate-180`]}
                                    />
                                )
                            }
                            return el
                        }
                    }
                })}
            />
        </ConfigProvider>
    )
}

export default forwardRef(BaseTable)
